import { Vehicle } from "../../../../../types/appTypes";
import { EquipmentCatalog, NewPriceData, ServerEquipmentPrice, ServerNewPrice, ServerNewPriceComponents } from "../../../../../types/newPriceTypes";
import { isValidNum } from "../../../../../utilities/typeGuard";
import { getCalculatedEquipmentTax, shouldCalculateEquipmentTax } from "./equipmentTax";

export function mapNewPriceCalculationComponents(data: ServerNewPriceComponents, currentData: NewPriceData | null, vehicle: Vehicle | null): NewPriceData {
  // TODO: Fix this->selected value, i never implemented this fully
  const currentSelectedNewPrice = currentData?.newPrices?.find(item => currentData.selectedNewPrice === item.id);
  const manualNewPrice = currentData?.newPrices?.find(np => np.id === 'MANUAL');

  const newPrices = manualNewPrice ? [...data.newPrices ?? [], manualNewPrice] : data.newPrices
  const newPrice = currentSelectedNewPrice ?? newPrices?.[0];
  const equipment = mapEquipment(data, newPrice ?? null, currentData, vehicle, newPrice?.amount ?? null)
  return {
    selectedNewPrice: newPrice?.id ?? null,
    newPrices: newPrices ?? [],
    equipment: equipment
  }
}

function mapEquipment(
  data: ServerNewPriceComponents,
  newPrice: ServerNewPrice | null,
  currentData: NewPriceData | null,
  vehicle: Vehicle | null,
  newPriceAmount: number | null
): EquipmentCatalog[] {
  const customEquipment = currentData?.equipment.filter(item => item.isCustom) ?? []
  const currentMountedEquipment = currentData?.equipment?.length ? new Set(
    currentData?.equipment.filter(item => item.mounted).map(eq => eq.code)
  ) : null

  const allEquipment = [...data.vinEquipment ?? [], ...data.potentialEquipment ?? []]
  const vinEquipmentCodes = new Set(data.vinEquipment?.map(eq => eq.code))

  let equipment: EquipmentCatalog[] = allEquipment.map(eq => {
    const price = getEquipmentPrice(eq.prices, vehicle, newPriceAmount);

    return {
      code: eq.code,
      name: eq.name,
      source: price?.source?.name ?? null,
      sourceId: price?.source?.id ?? null,
      category: eq.typeCategory ?? 2,
      amount: isValidNum(price?.amount) ? Math.ceil(price.amount) : null,
      isWithTax: price?.isWithTax ?? false,
      mounted: currentMountedEquipment ? currentMountedEquipment.has(eq.code) : vinEquipmentCodes.has(eq.code),

      isVinEquipment: vinEquipmentCodes.has(eq.code),
      packageContent: (eq.subEquipments?.map(item => item.code).filter(Boolean) ?? []) as string[],

      includedInNewPrice: false, // set inside applyNewPriceSelectionToEquipment
      isForceMounted: false, // set inside applyNewPriceSelectionToEquipment && applyPackagesToEquipment
      isForceMountedByNewPrice: false, // set inside applyPackagesToEquipment
      isForceMountedByPackage: false, // set inside applyNewPriceSelectionToEquipment

      includedInPackage: [], // set inside applyPackagesToEquipment
      isIncludedInMountedPackage: false, // set inside applyPackagesToEquipment
      isCustom: false,
    }
  })

  equipment = applyNewPriceSelectionToEquipment(newPrice, equipment)
  equipment = applyPackagesToEquipment(equipment)
  equipment = mergeCustom(equipment, customEquipment)
  return equipment
}

function getEquipmentPrice(prices: ServerEquipmentPrice[] | null, vehicle: Vehicle | null, newPriceAmount: number | null) {
  const shouldCalc = shouldCalculateEquipmentTax(vehicle)
  let lowestPrice = null;
  const lowPrioPrices = []
  for (const price of prices ?? []) {
    if (shouldCalc && !price.isWithTax) {
      price.isWithTax = true
      price.amount = getCalculatedEquipmentTax(vehicle, price.amount ?? 0, true, newPriceAmount ?? 0) ?? 0
    }
    if (price.prioritized) {
      lowestPrice = price;
      break;
    }
    // If id is Custom or DAT [1,2], then skip this price
    if ([1, 2].includes(price.source?.id ?? -1)) {
      lowPrioPrices.push(price)
      continue;
    }
    if ((price.amount ?? Infinity) < (lowestPrice?.amount ?? Infinity)) {
      lowestPrice = price
    }
  }
  if (!lowestPrice && lowPrioPrices.length) {
    for (const price of lowPrioPrices) {
      if ((price.amount ?? Infinity) < (lowestPrice?.amount ?? Infinity)) {
        lowestPrice = price
      }
    }
  }
  return lowestPrice
}

const norm = (c?: string | null) => (c ?? "").trim().toUpperCase();

function mergeCustom(serverEq: EquipmentCatalog[], customEq: EquipmentCatalog[]) {
  const equipmentCodeMap = new Map(serverEq.map(eq => [norm(eq.code), eq] as const));

  for (const custom of customEq) {
    const code = norm(custom.code);
    if (!code) continue;

    const existing = equipmentCodeMap.get(code);
    if (existing && !existing.isCustom) continue; // server wins on collision

    equipmentCodeMap.set(code, { ...custom, code, isCustom: true });
  }

  return [...equipmentCodeMap.values()];
}

export function applyNewPriceSelectionToEquipment(newPrice: ServerNewPrice | null, equipment: EquipmentCatalog[]) {
  const equipmentIncludedInPrice = new Set(newPrice?.equipmentIncludedInPrice);
  const equipmentForceMounted = new Set(newPrice?.equipmentForceMounted);
  return equipment.map(eq => {
    const forceMounted = equipmentForceMounted.has(eq.code ?? "") || equipmentIncludedInPrice.has(eq.code ?? "")
    return {
      ...eq,
      includedInNewPrice: equipmentIncludedInPrice.has(eq.code ?? ""),
      isForceMountedByNewPrice: forceMounted,
      isForceMounted: forceMounted,
    }
  })
}

// Curtesy of ChatGPT
export function applyPackagesToEquipment(equipment: EquipmentCatalog[]): EquipmentCatalog[] {
  const equipmentByCode = new Map<string, EquipmentCatalog>();
  const includedInPackageMap: Record<string, string[]> = {};

  for (const eq of equipment) {
    if (eq.code) {
      equipmentByCode.set(eq.code, eq);
    }
  }

  // Build reverse lookup: child equipment code -> package codes that include it
  for (const pkg of equipment) {
    if (!pkg.code || pkg.packageContent.length === 0) continue;

    for (const subCode of pkg.packageContent) {
      if (!subCode) continue;
      (includedInPackageMap[subCode] ??= []).push(pkg.code);
    }
  }

  // Find all equipment that must be force-mounted because they are inside
  // a mounted package, recursively including nested packages.
  const forceMountedCodes = new Set<string>();
  const visited = new Set<string>();

  function markPackageContentRecursively(packageCode: string) {
    if (visited.has(packageCode)) return;
    visited.add(packageCode);

    const pkg = equipmentByCode.get(packageCode);
    if (!pkg || pkg.packageContent.length === 0) return;

    for (const subCode of pkg.packageContent) {
      if (!subCode) continue;

      forceMountedCodes.add(subCode);

      const subEquipment = equipmentByCode.get(subCode);
      if (subEquipment?.packageContent?.length) {
        markPackageContentRecursively(subCode);
      }
    }
  }

  // Start recursion from mounted or already force-mounted packages
  for (const eq of equipment) {
    if (!eq.code || eq.packageContent.length === 0) continue;

    if (eq.mounted || eq.isForceMounted) {
      markPackageContentRecursively(eq.code);
    }
  }

  return equipment.map(eq => {
    if (!eq.code) return eq;
    const isForceMountedByPackage = forceMountedCodes.has(eq.code);
    return {
      ...eq,
      includedInPackage: includedInPackageMap[eq.code] ?? [],
      isIncludedInMountedPackage: forceMountedCodes.has(eq.code),
      isForceMountedByPackage: isForceMountedByPackage,
      isForceMounted: isForceMountedByPackage || eq.isForceMountedByNewPrice,
    };
  });
}
